import { useState } from 'react';
import { useResendCooldown } from './useResendCooldown';

const RESEND_STEPS_SECONDS = [30, 60, 120];

export interface UseOtpFlowParams {
  initialEmail?: string;
  /** Always resolves (enumeration-safe) — the backend never reveals eligibility. */
  onRequestOtp: (email: string) => Promise<void>;
  /** Throws on failure (invalid/expired/max-attempts); resolves once signed in. */
  onVerifyOtp: (params: { email: string; code: string }) => Promise<void>;
}

/**
 * The email->code state machine shared by OtpLoginPage (full page) and
 * PublicLoginDialog's embedded OTP mode (compact modal) — same steps, same
 * resend-cooldown/edit-email behavior, different chrome around it. Callers
 * own all rendering; this hook owns only state and the three actions.
 */
export function useOtpFlow({ initialEmail, onRequestOtp, onVerifyOtp }: UseOtpFlowParams) {
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState(initialEmail ?? '');
  const [code, setCode] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { countdown, isCoolingDown, start: startCooldown, reset: resetCooldown } = useResendCooldown(RESEND_STEPS_SECONDS);

  const requestCode = async () => {
    if (!email.trim()) return;
    setRequesting(true);
    setError(null);
    try {
      await onRequestOtp(email.trim());
      setStep('code');
      startCooldown();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setRequesting(false);
    }
  };

  /** Returns whether verification succeeded — callers that need to act on
   *  the outcome (e.g. close a dialog) must use this return value, not read
   *  `error`/`verifying` right after awaiting: those come from state set
   *  inside this same call, so a closure holds their pre-call values, not
   *  the ones just written. */
  const verifyCode = async (): Promise<boolean> => {
    if (!code.trim()) return false;
    setVerifying(true);
    setError(null);
    try {
      await onVerifyOtp({ email: email.trim(), code: code.trim() });
      return true;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'That code is no longer valid.');
      setVerifying(false);
      return false;
    }
  };

  const editEmail = () => {
    setStep('email');
    setCode('');
    setError(null);
    // Editing the email is a fresh request, not a repeat of the last one —
    // don't let it inherit the escalated 60s/120s cooldown tier.
    resetCooldown();
  };

  const resend = async () => {
    if (isCoolingDown || requesting) return;
    setRequesting(true);
    setError(null);
    try {
      await onRequestOtp(email.trim());
      startCooldown();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setRequesting(false);
    }
  };

  /** Clears every field, including email — for a caller (e.g. a dialog) that
   *  closes and must not show a previous attempt's state on next open. */
  const reset = () => {
    setStep('email');
    setEmail(initialEmail ?? '');
    setCode('');
    setError(null);
    resetCooldown();
  };

  return {
    step,
    isCodeStep: step === 'code',
    email,
    setEmail,
    code,
    setCode,
    requesting,
    verifying,
    error,
    isCoolingDown,
    countdown,
    requestCode,
    verifyCode,
    editEmail,
    resend,
    reset,
  };
}
